import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import agentService, {
    forceLogout,
    refreshAccessToken,
    setSessionExpiredHandler,
} from '../services/agentService';

const AuthContext = createContext(null);

/** Access token suresi dolmadan kac ms once yenilenmeye calisilir. */
const REFRESH_MARGIN_MS = 60_000;
/** Sure kontrolu araligi. setTimeout yerine interval: dizustu uyudugunda
 *  uzun timeout'lar guvenilir tetiklenmiyor, interval uyaninca yakaliyor. */
const CHECK_INTERVAL_MS = 30_000;

/**
 * JWT payload'ini decode eder.
 *
 * atob() base64url guvenli DEGIL: '-' ve '_' karakterlerinde patlar ve
 * cikti latin1 oldugu icin Turkce karakterli bir 'sub' bozulur. Bu yuzden
 * once base64url -> base64 cevrilir, sonra UTF-8 olarak decode edilir.
 */
const decodeToken = (token) => {
    try {
        const part = token.split('.')[1];
        if (!part) return null;
        const padded = part.replace(/-/g, '+').replace(/_/g, '/');
        const b64 = padded + '='.repeat((4 - (padded.length % 4)) % 4);
        const bin = atob(b64);
        const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
        return JSON.parse(new TextDecoder().decode(bytes));
    } catch {
        return null;
    }
};

/** exp saniye cinsinden; yoksa suresi belirsiz kabul edilir (gecerli sayilmaz). */
const isExpired = (payload, marginMs = 0) => {
    if (!payload?.exp) return true;
    return payload.exp * 1000 - marginMs <= Date.now();
};

const toAgent = (payload) => ({
    id: payload.agentId,
    username: payload.sub,
    role: payload.role,
    displayName: payload.sub, // Token'da displayName yok, username kullan
});

/** localStorage'daki token'dan gecerli bir agent cikarir; yoksa temizler. */
const readStoredAgent = () => {
    const token = localStorage.getItem('token');
    if (!token) return null;

    const payload = decodeToken(token);
    // SURESI GECMIS TOKEN ARTIK "GIRISLI" SAYILMIYOR. Onceden decode
    // basarili oldugu icin agent set ediliyor, panel aciliyor, tum sayfalar
    // 401 aliyor ve kullanici once hata bildirimleri goruyordu.
    if (!payload || isExpired(payload)) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        return null;
    }
    return toAgent(payload);
};

export const AuthProvider = ({ children }) => {
    // Sayfa yenilendiginde token varsa agent bilgisini token'dan cikart
    const [agent, setAgent] = useState(readStoredAgent);

    const login = useCallback(async (username, password) => {
        const data = await agentService.login(username, password);
        const token = data.token;

        localStorage.setItem('token', token);
        if (data.refreshToken) {
            localStorage.setItem('refreshToken', data.refreshToken);
        }

        // Token'dan kullanici bilgilerini cikart
        const payload = decodeToken(token);
        if (!payload) {
            // Bozuk token: sessizce "girisli" gorunmek yerine acikca hata ver
            throw new Error('Gecersiz token alindi');
        }
        const agentData = toAgent(payload);

        setAgent(agentData);
        return agentData;
    }, []);

    const logout = useCallback(async () => {
        if (agent) {
            try {
                await agentService.logout();
            } catch (err) {
                console.error('Logout hatasi:', err);
            }
        }
        setAgent(null);
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
    }, [agent]);

    // forceLogout (agentService) cagrildiginda React state'i de temizlenir.
    useEffect(() => {
        setSessionExpiredHandler(() => setAgent(null));
        return () => setSessionExpiredHandler(null);
    }, []);

    // Proaktif yenileme: access token 15 dakikada doluyor. Suresi bitmeden
    // yenileyerek kullanicinin 401 ve hata bildirimi gormesini engelliyoruz.
    // Yenileme basarisiz olursa (refresh token da dolmus/iptal) oturum kapanir
    // ve /login'e yonlendirilir.
    useEffect(() => {
        if (!agent) return;

        let cancelled = false;

        const check = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                if (!cancelled) forceLogout();
                return;
            }
            const payload = decodeToken(token);
            if (!payload || isExpired(payload)) {
                if (!cancelled) forceLogout();
                return;
            }
            if (isExpired(payload, REFRESH_MARGIN_MS)) {
                try {
                    await refreshAccessToken();
                } catch {
                    if (!cancelled) forceLogout();
                }
            }
        };

        check();
        const id = setInterval(check, CHECK_INTERVAL_MS);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, [agent]);

    return (
        <AuthContext.Provider value={{ agent, login, logout, isAuthenticated: !!agent }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};
